import {
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
  startTransition,
} from "react";
import type { NodeViewProps } from "@tiptap/react";
import { NodeViewWrapper } from "@tiptap/react";
import type { Variables } from "@/lib/sparxs/metadataSchemas";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/molecules/popover";
import { RTE_NO_VARIABLES } from "@/constants/uiCopy/richTextEditor";
import {
  CHIP_CLASSES,
  PICKER_LIST_POPOVER_CLASS,
} from "./variableTokenConstants";
import { DirectiveSegmentPickerList } from "@/features/sparxsEditor/components/organisms/richTextEditor/directives/builder/panels/segmentPickerList";
import { updateRawAtPos } from "./variableTokenNodeViewHelpers";

export const VariableTokenNodeViewSimple = ({
  node,
  editor,
  getPos,
}: NodeViewProps) => {
  const [open, setOpen] = useState(false);
  const [highlightOverride, setHighlightOverride] = useState<number | null>(
    null,
  );
  const chipPickerKeyboardRef = useRef<HTMLDivElement>(null);
  let raw = node.attrs.raw as string;
  if (/^\{\{.*\}\}$/.test(raw)) raw = raw.slice(2, -2);

  const storage = editor.storage as {
    variableToken?: { getVariables: () => Variables[] };
  };
  const allVariables = useMemo(
    () => storage.variableToken?.getVariables() ?? [],
    [storage.variableToken],
  );

  const pickerItems = useMemo(
    () => allVariables.map((v) => ({ raw: v.name, label: v.name })),
    [allVariables],
  );

  const derivedBaseIndex = useMemo(() => {
    const i = pickerItems.findIndex((it) => it.raw === raw);
    return i >= 0 ? i : 0;
  }, [pickerItems, raw]);

  useEffect(() => {
    startTransition(() => {
      setHighlightOverride(null);
    });
  }, [derivedBaseIndex, open]);

  const chipPickerIndex = highlightOverride ?? derivedBaseIndex;

  const setChipPickerIndex = useCallback(
    (update: number | ((prev: number) => number)) => {
      setHighlightOverride((prev) => {
        const effective = prev ?? derivedBaseIndex;
        return typeof update === "function" ? update(effective) : update;
      });
    },
    [derivedBaseIndex],
  );

  const handleSelect = useCallback(
    (variableName: string) => {
      updateRawAtPos(editor, getPos, variableName);
      setOpen(false);
    },
    [editor, getPos],
  );

  return (
    <NodeViewWrapper as="span" contentEditable={false} className="inline">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <span
            className={CHIP_CLASSES}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setOpen((prev) => !prev);
              }
            }}
          >
            {raw}
          </span>
        </PopoverTrigger>
        <PopoverContent
          dropdownPanel
          dropdownPanelScrollable
          className="w-auto max-w-[320px]"
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            requestAnimationFrame(() => {
              chipPickerKeyboardRef.current?.focus();
            });
          }}
        >
          <DirectiveSegmentPickerList
            containerRef={chipPickerKeyboardRef}
            items={pickerItems}
            selectedIndex={chipPickerIndex}
            onHighlightChange={setChipPickerIndex}
            onSelect={(item) => {
              handleSelect(item.raw);
            }}
            onEscape={() => {
              setOpen(false);
            }}
            optionIdPrefix="chip-simple-var"
            className={PICKER_LIST_POPOVER_CLASS}
            emptyMessage={RTE_NO_VARIABLES}
          />
        </PopoverContent>
      </Popover>
    </NodeViewWrapper>
  );
};
